import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { useProposal } from '../../../context/proposalContext/ProposalContextProvider';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

const StandardTotal = () => {
  const { proposal, setProposal } = useProposal();
  const { quoteProducts, priceGstInclude } = proposal;
  const [expandedSection, setExpandedSection] = useState('standardTotal'); // Default to "Standard Total"
  const { standardTotal, alternateTotal } = proposal;

  const formatValue = useCallback(
    (value) =>
      value !== undefined && value !== null && !isNaN(value)
        ? parseFloat(value).toFixed(2)
        : '0.00',
    [],
  );

  const toggleSection = (section) => {
    if (expandedSection !== section) {
      setExpandedSection(section); // Ensure one section is always open
    }
  };

  // Corrected: calculateCurrentSupplierInformation (Current Supplier)
  const calculateCurrentSupplierInformation = useCallback(
    (products) => {
      let subtotal = 0; // includes exempt products
      let taxableSubtotal = 0; // only GST applicable products
      let total = 0;
      let gstTotal = 0;

      products.forEach((product) => {
        const qty = parseFloat(product.quantity || 1);
        const taxCode = product.mainProduct.taxCode; // "E" means exempt

        if (!priceGstInclude) {
          const exc = parseFloat(
            product.mainProductCurrentSupplierExcGstTotal || 0,
          );
          subtotal += exc; // includes exempt
          if (taxCode !== 'E') {
            taxableSubtotal += exc;
          }
        } else {
          // Inclusive → CCP INC price
          const inc = parseFloat(
            product.mainProductCurrentSupplierIncGstPriceTotal || 0,
          );
          total += inc; // includes exempt
          if (taxCode !== 'E') {
            taxableSubtotal += inc;
          }
        }
      });
      if (!priceGstInclude) {
        gstTotal = taxableSubtotal * 0.1;
        total = subtotal + gstTotal;
      } else {
        gstTotal = (taxableSubtotal / 1.1) * 0.1;
        subtotal = total - gstTotal;
      }

      return {
        subtotal: parseFloat(subtotal.toFixed(2)),
        gstTotal: parseFloat(gstTotal.toFixed(2)),
        total: parseFloat(total.toFixed(2)),
      };
    },
    [priceGstInclude],
  );

  const calculateGPInformation = useCallback((products) => {
    let totalGpRequired = 0;
    let totalCurSupplierGp = 0;
    const count = products.length;

    products.forEach((product) => {
      totalGpRequired += parseFloat(product.mainProductGpRequired || 0);
      totalCurSupplierGp += parseFloat(
        product.mainProductCurrentSupplierGp || 0,
      );
    });

    const avgOfGPRequired =
      parseFloat((totalGpRequired / count).toFixed(2)) || 0;
    const avgOfCurSupplierGP =
      parseFloat((totalCurSupplierGp / count).toFixed(2)) || 0;

    return { avgOfGPRequired, avgOfCurSupplierGP };
  }, []);

  // Corrected: calculateTotalInformation (Jaybel)
  const calculateTotalInformation = useCallback(
    (products) => {
      let subtotal = 0; // includes exempt products
      let taxableSubtotal = 0; // only GST applicable products
      let total = 0;
      let gstTotal = 0;

      products.forEach((product) => {
        const qty = parseFloat(product.quantity || 1);
        const taxCode = product.mainProduct.taxCode; // "E" means exempt

        if (!priceGstInclude) {
          // Exclusive → JP EXC price
          const exc = parseFloat(
            product.mainProductJaybelExcGstPriceTotal || 0,
          );
          subtotal += exc; // includes exempt
          if (taxCode !== 'E') {
            taxableSubtotal += exc;
          }
        } else {
          // Inclusive → JP INC price
          const inc = parseFloat(
            product.mainProductJaybelIncGstPriceTotal || 0,
          );
          total += inc; // includes exempt
          if (taxCode !== 'E') {
            taxableSubtotal += inc;
          }
        }
      });

      if (!priceGstInclude) {
        gstTotal = taxableSubtotal * 0.1;
        total = subtotal + gstTotal;
      } else {
        gstTotal = (taxableSubtotal / 1.1) * 0.1;
        subtotal = total - gstTotal;
      }

      return {
        subtotal: parseFloat(subtotal.toFixed(2)),
        gstTotal: parseFloat(gstTotal.toFixed(2)),
        total: parseFloat(total.toFixed(2)),
      };
    },
    [priceGstInclude],
  );

  const calculateSavingsInBuyingFromJaybel = useCallback(
    (currentSupplierTotal, totalInformation, products) => {
      // If priceGstInclude is false (exclusive), use total; if true (inclusive), use subtotal
      let overallSavingDollar = 0;
      let denominator = 0;
      if (!priceGstInclude) {
        overallSavingDollar =
          //exclusive
          parseFloat(
            (currentSupplierTotal.subtotal - totalInformation.subtotal).toFixed(
              2,
            ),
          ) || 0;
        denominator = currentSupplierTotal.subtotal;
      } else {
        overallSavingDollar =
          //inclusive
          parseFloat(
            (currentSupplierTotal.total - totalInformation.total).toFixed(2),
          ) || 0;
        denominator = currentSupplierTotal.total;
      }

      const totalSavingPercent = products.reduce(
        (acc, product) => acc + parseFloat(product.mainProductSaving || 0),
        0,
      );

      const overallSavingPercent =
        denominator !== 0
          ? parseFloat(((overallSavingDollar / denominator) * 100).toFixed(2))
          : 0;

      const totalAnnualSavingDollar =
        parseFloat((overallSavingDollar * 12).toFixed(2)) || 0;

      return {
        overallSavingDollar,
        overallSavingPercent,
        totalAnnualSavingDollar,
      };
    },
    [priceGstInclude],
  );

  const calculateAlternateProductInformation = useCallback(
    (products) => {
      const alternateProducts = products.filter((p) => p.alternateProduct);
      if (alternateProducts.length === 0) {
        return {
          currentSupplierInformation: { subtotal: 0, gstTotal: 0, total: 0 },
          gpInformation: { avgOfGPRequired: 0, avgOfCurSupplierGP: 0 },
          totalInformation: { subtotal: 0, gstTotal: 0, total: 0 },
          savingsInBuyingFromJaybel: {
            overallSavingDollar: 0,
            overallSavingPercent: 0,
            totalAnnualSavingDollar: 0,
          },
        };
      }

      const mappedProducts = products.map((p) => ({
        ...p,

        // IMPORTANT for GST exemption

        mainProduct: p.alternateProduct || p.mainProduct,

        mainProductCurrentSupplierExcGstTotal:
          p.alternateProductCurrentSupplierExcGstTotal ||
          p.mainProductCurrentSupplierExcGstTotal,

        mainProductCurrentSupplierIncGstPriceTotal:
          p.alternateProductCurrentSupplierIncGstPriceTotal ||
          p.mainProductCurrentSupplierIncGstPriceTotal,

        mainProductGpRequired:
          p.alternateProductGpRequired || p.mainProductGpRequired,

        mainProductCurrentSupplierGp:
          p.alternateProductCurrentSupplierGp || p.mainProductCurrentSupplierGp,

        mainProductSaving: p.alternateProductSaving || p.mainProductSaving,

        mainProductJaybelExcGstPriceTotal:
          p.alternateProductJaybelExcGstPriceTotal ||
          p.mainProductJaybelExcGstPriceTotal,

        mainProductJaybelIncGstPriceTotal:
          p.alternateProductJaybelIncGstPriceTotal ||
          p.mainProductJaybelIncGstPriceTotal,
      }));

      const currentSupplierInformation =
        calculateCurrentSupplierInformation(mappedProducts);
      const gpInformation = calculateGPInformation(mappedProducts);
      const totalInformation = calculateTotalInformation(mappedProducts);
      const savingsInBuyingFromJaybel = calculateSavingsInBuyingFromJaybel(
        currentSupplierInformation,
        totalInformation,
        mappedProducts,
      );

      return {
        currentSupplierInformation,
        gpInformation,
        totalInformation,
        savingsInBuyingFromJaybel,
      };
    },
    [
      calculateCurrentSupplierInformation,
      calculateGPInformation,
      calculateTotalInformation,
      calculateSavingsInBuyingFromJaybel,
    ],
  );

  const updateProposalCalculations = useCallback(() => {
    // Main Product Calculations
    const mainCurrentSupplierInformation =
      calculateCurrentSupplierInformation(quoteProducts); // Use quoteProducts
    const mainGpInformation = calculateGPInformation(quoteProducts);
    const mainTotalInformation = calculateTotalInformation(quoteProducts);
    const mainSavingsInBuyingFromJaybel = calculateSavingsInBuyingFromJaybel(
      mainCurrentSupplierInformation,
      mainTotalInformation,
      quoteProducts,
    );

    // Alternate Product Calculations
    const alternateTotal = calculateAlternateProductInformation(quoteProducts); // Fully isolated logic

    // Update Proposal State
    setProposal((prevProposal) => ({
      ...prevProposal,
      standardTotal: {
        id: prevProposal?.standardTotal?.id,
        currentSupplierInformation: {
          id: prevProposal?.standardTotal?.currentSupplierInformation?.id,
          ...mainCurrentSupplierInformation,
        },
        gpInformation: {
          id: prevProposal?.standardTotal?.gpInformation?.id,
          ...mainGpInformation,
        },
        totalInformation: {
          id: prevProposal?.standardTotal?.totalInformation?.id,
          ...mainTotalInformation,
        },
        savingsInBuyingFromJaybel: {
          id: prevProposal?.standardTotal?.savingsInBuyingFromJaybel?.id,
          ...mainSavingsInBuyingFromJaybel,
        },
      },
      alternateTotal: {
        id: prevProposal?.alternateTotal?.id,
        ...alternateTotal,
      }, // Already calculated independently
    }));
  }, [
    quoteProducts,
    calculateCurrentSupplierInformation,
    calculateGPInformation,
    calculateTotalInformation,
    calculateSavingsInBuyingFromJaybel,
    calculateAlternateProductInformation,
    setProposal,
  ]);

  useEffect(() => {
    updateProposalCalculations();
  }, [quoteProducts, updateProposalCalculations]);

  const renderStandardTotal = () => (
    <>
      <div className="font-400 p-1 rounded-lg text-sm">
        <div className="text-center font-700">Current Supplier Total</div>
        <div className="flex justify-between">
          <div>Subtotal</div>
          <div>
            $ {formatValue(standardTotal?.currentSupplierInformation?.subtotal)}
          </div>
        </div>
        <div className="flex justify-between">
          <div>Gst Total</div>
          <div>
            $ {formatValue(standardTotal?.currentSupplierInformation?.gstTotal)}
          </div>
        </div>
        <div className="flex justify-between">
          <div>Total</div>
          <div>
            $ {formatValue(standardTotal?.currentSupplierInformation?.total)}
          </div>
        </div>
      </div>
      <div className="font-400 p-1 rounded-lg text-sm">
        <div className="text-center font-700">GP Information</div>
        <div className="flex justify-between">
          <div>Avg Gp Required</div>
          <div>
            {formatValue(standardTotal?.gpInformation?.avgOfGPRequired)} %
          </div>
        </div>
        <div className="flex justify-between">
          <div>Avg Cur Supplier GP</div>
          <div>
            {formatValue(standardTotal?.gpInformation?.avgOfCurSupplierGP)} %
          </div>
        </div>
      </div>
      <div className="font-400 p-1 rounded-lg text-sm">
        <div className="text-center font-700">Jaybel Total</div>
        <div className="flex justify-between">
          <div>Subtotal</div>
          <div>$ {formatValue(standardTotal?.totalInformation?.subtotal)}</div>
        </div>
        <div className="flex justify-between">
          <div>Gst Total</div>
          <div>$ {formatValue(standardTotal?.totalInformation?.gstTotal)}</div>
        </div>
        <div className="flex justify-between">
          <div>Total</div>
          <div>$ {formatValue(standardTotal?.totalInformation?.total)}</div>
        </div>
      </div>
      <div className="font-400 p-1 rounded-lg text-sm">
        <div className="text-center font-700">Total Saving</div>
        <div className="flex justify-between">
          <div>Saving in $</div>
          <div>
            ${' '}
            {formatValue(
              standardTotal?.savingsInBuyingFromJaybel?.overallSavingDollar,
            )}
          </div>
        </div>
        <div className="flex justify-between">
          <div>Saving in %</div>
          <div>
            {formatValue(
              standardTotal?.savingsInBuyingFromJaybel?.overallSavingPercent,
            )}{' '}
            %
          </div>
        </div>
        <div className="flex justify-between">
          <div>Annual Saving</div>
          <div>
            ${' '}
            {formatValue(
              standardTotal?.savingsInBuyingFromJaybel?.totalAnnualSavingDollar,
            )}
          </div>
        </div>
      </div>
    </>
  );

  const renderAlternateTotal = () => (
    <>
      <div className="font-400 p-1 rounded-lg text-sm">
        <div className="text-center font-700">Current Supplier Total</div>
        <div className="flex justify-between">
          <div>Subtotal</div>
          <div>
            ${' '}
            {formatValue(alternateTotal?.currentSupplierInformation?.subtotal)}
          </div>
        </div>
        <div className="flex justify-between">
          <div>Gst Total</div>
          <div>
            ${' '}
            {formatValue(alternateTotal?.currentSupplierInformation?.gstTotal)}
          </div>
        </div>
        <div className="flex justify-between">
          <div>Total</div>
          <div>
            $ {formatValue(alternateTotal?.currentSupplierInformation?.total)}
          </div>
        </div>
      </div>
      <div className="font-400 p-1 rounded-lg text-sm">
        <div className="text-center font-700">GP Information</div>
        <div className="flex justify-between">
          <div>Avg Gp Required</div>
          <div>
            {formatValue(alternateTotal?.gpInformation?.avgOfGPRequired || 0)} %
          </div>
        </div>
        <div className="flex justify-between">
          <div>Avg Cur Supplier GP</div>
          <div>
            {formatValue(
              alternateTotal?.gpInformation?.avgOfCurSupplierGP || 0,
            )}{' '}
            %
          </div>
        </div>
      </div>
      <div className="font-400 p-1 rounded-lg text-sm">
        <div className="text-center font-700">Jaybel Total</div>
        <div className="flex justify-between">
          <div>Subtotal</div>
          <div>$ {formatValue(alternateTotal?.totalInformation?.subtotal)}</div>
        </div>
        <div className="flex justify-between">
          <div>Gst Total</div>
          <div>$ {formatValue(alternateTotal?.totalInformation?.gstTotal)}</div>
        </div>
        <div className="flex justify-between">
          <div>Total</div>
          <div>$ {formatValue(alternateTotal?.totalInformation?.total)}</div>
        </div>
      </div>
      <div className="font-400 p-1 rounded-lg text-sm">
        <div className="text-center font-700">Total Saving</div>
        <div className="flex justify-between">
          <div>Saving in $</div>
          <div>
            ${' '}
            {formatValue(
              alternateTotal?.savingsInBuyingFromJaybel?.overallSavingDollar,
            )}
          </div>
        </div>
        <div className="flex justify-between">
          <div>Saving in %</div>
          <div>
            {formatValue(
              alternateTotal?.savingsInBuyingFromJaybel?.overallSavingPercent ||
                0,
            )}{' '}
            %
          </div>
        </div>
        <div className="flex justify-between">
          <div>Annual Saving</div>
          <div>
            ${' '}
            {formatValue(
              alternateTotal?.savingsInBuyingFromJaybel
                ?.totalAnnualSavingDollar,
            )}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="mx-5 shadow-2xl p-3 rounded-xl w-full mt-10 border border-gray-300">
      <div
        className={`flex justify-between items-center text-base font-semibold py-2 px-3 cursor-pointer ${
          expandedSection === 'standardTotal' ? 'bg-blue-100' : 'bg-gray-100'
        }`}
        onClick={() => toggleSection('standardTotal')}>
        <span>Standard Total</span>
        {expandedSection === 'standardTotal' ? (
          <FaChevronUp className="text-gray-600" />
        ) : (
          <FaChevronDown className="text-gray-600" />
        )}
      </div>
      {expandedSection === 'standardTotal' && (
        <div className="p-3 border border-gray-200 rounded-b-lg text-sm">
          {renderStandardTotal()}
        </div>
      )}
      {quoteProducts.some((p) => p.alternateProduct) && (
        <>
          <div
            className={`flex justify-between items-center text-base font-semibold py-2 px-3 cursor-pointer mt-3 ${
              expandedSection === 'alternateTotal'
                ? 'bg-blue-100'
                : 'bg-gray-100'
            }`}
            onClick={() => toggleSection('alternateTotal')}>
            <span>Alternate Total</span>
            {expandedSection === 'alternateTotal' ? (
              <FaChevronUp className="text-gray-600" />
            ) : (
              <FaChevronDown className="text-gray-600" />
            )}
          </div>
          {expandedSection === 'alternateTotal' && (
            <div className="p-3 border border-gray-200 rounded-b-lg text-sm">
              {renderAlternateTotal()}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StandardTotal;
